import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, from, map, Observable, of, switchMap} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {z} from 'zod';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {environment} from 'src/environments/environment';
import {mapParseItem, openBlobInNewTabOrDownload, TRANSLATE} from '@brunata-angular/util';

export const ACTIVATION_LETTER_FILE_NAME = 'activationletters.pdf';

@Injectable({
    providedIn: 'root',
})
export class ConsumerInviteService {
    private readonly http = inject(HttpClient);
    private readonly confirmationDialog = inject(ConfirmationDialogService);
    private readonly toastr = inject(ToastrService);
    private readonly translate = inject(TRANSLATE);

    /**
     * Confirms the invite, sends it, and then opens the activation letter or shows the toast.
     * Emits whether the caller should reload - true on the failure path too, because a failed
     * run can still have committed the invite.
     */
    inviteWithConfirmation(request: ConsumerInviteRequest): Observable<boolean> {
        const hasEmail = (request.email ?? '').trim().length > 0;
        return from(this.confirmInvite(request.labelKey, hasEmail)).pipe(
            switchMap(confirmed => {
                if (!confirmed) {
                    return of(false);
                }
                return this.inviteConsumer(
                    request.buildingNo,
                    request.locationId,
                    request.consumerId,
                    hasEmail ? 'email' : 'pdf',
                ).pipe(
                    map(pdf => {
                        this.announceInvite(pdf);
                        return true;
                    }),
                    // The error toast comes from the global ErrorInterceptor.
                    catchError(() => of(true)),
                );
            }),
        );
    }

    private confirmInvite(labelKey: string, hasEmail: boolean): Promise<boolean> {
        const action = this.translate(labelKey, 'online', 'residents');
        return this.confirmationDialog.confirm(
            action,
            [
                `<p>${this.translate('information_toActivate', 'online', 'residents')}</p>`,
                `<p><strong>${this.translate(
                    hasEmail ? 'information_youHaveTheEmail' : 'information_ifYouHaveEmail',
                    'online',
                    'residents',
                )}</strong></p>`,
            ].join(''),
            action,
            this.translate('actions_cancel', 'online', 'common'),
        );
    }

    private announceInvite(pdf: Blob | null): void {
        if (pdf != null) {
            openBlobInNewTabOrDownload(ACTIVATION_LETTER_FILE_NAME, pdf);
            return;
        }
        this.toastr.success(this.translate('alerts_activationEmailSent', 'online', 'residents'));
    }

    private inviteConsumer(
        buildingNo: string,
        locationId: string,
        consumerId: string,
        media: InviteMedia,
    ): Observable<Blob | null> {
        return this.http
            .post(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/invite`,
                null,
                {params: {media}, responseType: 'blob'},
            )
            .pipe(map(pdf => (media === 'pdf' && pdf.size > 0 ? pdf : null)));
    }

    getBatchSignUpDetails(buildingNo: string): Observable<BatchSignUpDetails> {
        return this.http
            .get(`${environment.onlineServiceUrl}/buildings/${buildingNo}/batchsignupconsumers`)
            .pipe(mapParseItem(batchSignUpDetailsSchema));
    }

    inviteAllConsumers(buildingNo: string): Observable<Blob | null> {
        return this.http
            .post(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/batchsignupconsumers`,
                null,
                {params: {byemail: true}, responseType: 'blob'},
            )
            .pipe(map(pdf => (pdf.size > 0 ? pdf : null)));
    }
}

const batchSignUpDetailsSchema = z.object({
    consumersCount: z.number(),
    emailCount: z.number(),
});

export type BatchSignUpDetails = z.infer<typeof batchSignUpDetailsSchema>;

type InviteMedia = 'email' | 'pdf';

export type ConsumerInviteRequest = {
    buildingNo: string;
    locationId: string;
    consumerId: string;
    email: string | null;
    /** Translation key of the invite/resend action, used as the dialog title and confirm label. */
    labelKey: string;
};
